import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Button, Stack, Divider } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { getDecryptDataFromLocal } from "../../../utils/encrypt";
import DeliveryCenterChatDrawer from "./DeliveryCenterChatDrawer";
import { SoftphoneButton } from "../../../ui-component/SoftphoneCall";

const POLL_MS = 10 * 60 * 1000; // 10 min baseline (Shailendra 2026-09-24, was 30s) — fastPoll below still covers the time-sensitive unlock case
const EXEMPT_PATH = "/bulk/delivery-center-orders";

// Agent/TL/manager (role 5/13/14) RTO-risk SLA gate: once SCM marks an order "Chance of RTO"
// or manually locks it, this blocks the dashboard (except the Delivery Center Orders tab) until
// every breached order tied to that person's scope gets a reply. Each role only sees orders in
// their own downward org scope (agent = just their own orders, TL = their team, manager = their
// whole downline), so a single order can lock the agent, their TL, and their manager at once.
// Three ways an order ends up here:
//  1. No agent/manager reply at all for over 6 working hours after SCM's last message.
//  2. SCM (Bhavna/Kajal) marked the manager's existing reply "invalid" in chat, and
//     1 hour passed with no valid reply since (o.invalidReply flag below).
//  3. SCM (Harshit) manually locked the order in the sheet — instant, no timer — and no
//     reply has been sent since the lock (o.manualLock flag below).
// Mirrors the full-screen blocking style already used by UnregisteredLeadsPopup for role 5.
function RtoRiskLockPopup() {
  const role = Number(getDecryptDataFromLocal("setRole"));
  const location = useLocation();
  const history = useHistory();
  const [breachedOrders, setBreachedOrders] = useState([]);
  const [chatOrderId, setChatOrderId] = useState(null);
  // Optimistic unlock — set the instant the recording-unlock Call button's dial API call
  // succeeds (SoftphoneButton's onCallPlaced), so the screen opens immediately instead of
  // waiting on a webhook + poll round-trip ("if we can know agent start dial then open the
  // dashboard immediately"). fetchStatus() still runs right after to reconcile with the real
  // backend state (and the 5s poll below keeps it honest — e.g. re-locks after the 30-min grace
  // window if the call never actually connects).
  const [justDialedOrderIds, setJustDialedOrderIds] = useState(() => new Set());
  const timerRef = useRef(null);

  const isManager = role === 5 || role === 13 || role === 14;

  const fetchStatus = () => {
    if (!isManager) return;
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getManagerRtoLockStatus`, {})
      .then((res) => {
        if (res.data.success) {
          setBreachedOrders(res.data.data?.breachedOrders || []);
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (!isManager) return;
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SCM flagged a message/chat "recording needed" (see markDeliveryCenterChatRecordingRequired
  // + the recording_required path in getManagerRtoLockStatus) — a call-only screen replaces the
  // usual reply-required list while any of these are outstanding. Only role 5 (Agent) gets the
  // actionable Call button ("now agent will dial the call") — TL just sees a waiting state.
  // justDialedOrderIds (optimistic unlock, set below) hides an order here immediately once its
  // Call button's dial succeeds, ahead of backend confirmation.
  const recordingOrders = breachedOrders.filter((o) => o.reason === "recording_required" && !justDialedOrderIds.has(o._id));
  const otherOrders = breachedOrders.filter((o) => o.reason !== "recording_required");
  const showRecordingScreen = recordingOrders.length > 0;

  const handleCallPlaced = (orderId) => {
    setJustDialedOrderIds((prev) => new Set(prev).add(orderId));
    // Give the webhook + a couple of fast polls time to land, then drop back to relying on
    // the real backend state — if the call genuinely never got logged (e.g. it targeted the
    // wrong number), the lock reasserts itself instead of staying optimistically open forever.
    setTimeout(() => {
      setJustDialedOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }, 90 * 1000);
    fetchStatus();
  };

  // Poll faster (5s vs the normal 30s) while a recording-required lock is showing, or right
  // after a dial was just optimistically accepted, so the dashboard's real state catches up
  // promptly — getManagerRtoLockStatus treats ANY call log against the tagged custom_id
  // (connected or not, and now also a phone-number fallback match) as satisfying the 30-min grace
  // window, so this just needs to catch the webhook write quickly, not wait a full 30s poll.
  const fastPoll = showRecordingScreen || justDialedOrderIds.size > 0;
  useEffect(() => {
    if (!isManager) return undefined;
    const ms = fastPoll ? 5000 : POLL_MS;
    timerRef.current = setInterval(fetchStatus, ms);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager, fastPoll]);

  if (!isManager || breachedOrders.length === 0) return null;

  const onExemptPage = location.pathname === EXEMPT_PATH;

  return (
    <>
      {!onExemptPage && showRecordingScreen && (
        <Box
          sx={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: "rgba(0,0,0,0.9)", zIndex: 1300,
            display: "flex", alignItems: "center", justifyContent: "center", p: 2,
          }}
        >
          <Paper sx={{ maxWidth: 480, width: "100%", maxHeight: "85vh", overflowY: "auto", p: 3, textAlign: "center" }}>
            <WarningAmberIcon color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
              Call Recording Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              SCM needs a recording of your call with the buyer before your dashboard unlocks. If your call
              doesn't connect, the dashboard opens for 30 minutes then locks again — you'll need to call once more.
            </Typography>
            <Stack spacing={3} divider={<Divider />}>
              {recordingOrders.map((o) => (
                <Stack key={o._id} spacing={1} alignItems="center">
                  <Typography variant="subtitle2">
                    {o.customerName} {o.invoiceNumber ? `(${o.invoiceNumber})` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Agent: {o.agentName || "-"} — flagged by SCM ({o.requiredByPhone}) {moment(o.dispositionMarkedAt).format("DD MMM, hh:mm A")}
                  </Typography>
                  {role === 5 ? (
                    <>
                      <Box
                        sx={{
                          width: 88, height: 88, borderRadius: "50%", bgcolor: "#c62828",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: 3, mt: 1,
                        }}
                      >
                        <SoftphoneButton
                          number={o.callTargetPhone}
                          customId={o.recordingCallCustomId}
                          title={`Call buyer ${o.callTargetPhone} to unlock`}
                          color="#fff"
                          onCallPlaced={() => handleCallPlaced(o._id)}
                        />
                      </Box>
                      <Typography variant="caption" fontWeight={700} sx={{ color: "#c62828" }}>
                        Call the buyer ({o.callTargetPhone})
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Waiting for the agent to call the buyer ({o.callTargetPhone})
                    </Typography>
                  )}
                  <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setChatOrderId(o._id)}>
                    View Chat
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {!onExemptPage && !showRecordingScreen && otherOrders.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.85)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Paper sx={{ maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto", p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <WarningAmberIcon color="error" />
              <Typography variant="h6" fontWeight={700}>
                RTO Risk — Reply Required
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {otherOrders.length} order{otherOrders.length > 1 ? "s" : ""} assigned to you or your team{" "}
              {otherOrders.length > 1 ? "are" : "is"} on RTO risk — either no reply for over 6 working hours
              (office hours 9:30 AM - 6:30 PM, all days), SCM marked your reply invalid and 1 hour passed with
              no valid reply since, or SCM manually locked the order and is awaiting your reply. Please respond
              in chat for each order below. Your dashboard stays locked to this tab until all of them are cleared.
            </Typography>
            <Stack spacing={1.5} divider={<Divider />}>
              {otherOrders.map((o) => (
                <Stack key={o._id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="subtitle2">
                      {o.customerName} {o.invoiceNumber ? `(${o.invoiceNumber})` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Agent: {o.agentName || "-"} |{" "}
                      {o.manualLock ? "SCM locked this order at" : o.invalidReply ? "Reply marked invalid at" : "Last SCM message"}{" "}
                      {moment(o.dispositionMarkedAt).format("DD MMM, hh:mm A")} — {o.hoursOverdue}h overdue
                    </Typography>
                  </Box>
                  <Button size="small" variant="contained" color="error" onClick={() => setChatOrderId(o._id)}>
                    Reply Now
                  </Button>
                </Stack>
              ))}
            </Stack>
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              fullWidth
              onClick={() => history.push(EXEMPT_PATH)}
            >
              Open Delivery Center Orders
            </Button>
          </Paper>
        </Box>
      )}

      <DeliveryCenterChatDrawer
        open={Boolean(chatOrderId)}
        orderId={chatOrderId}
        zIndex={1400}
        onClose={() => {
          setChatOrderId(null);
          fetchStatus();
        }}
      />
    </>
  );
}

export default RtoRiskLockPopup;
